// Gemeinsame Bausteine der ueberarbeiteten Finanzoberflaeche (nur Darstellung): Seitenkopf, Kennzahl-Kachel, Hinweisleiste der Parallelbuchhaltung,
// responsive Hilfen. Verwendet ausschliesslich die vorhandenen THEME-Tokens (dunkles ComeFit-Design, keine Verlaeufe/Effekte).
import { useState, useEffect } from "react";
import THEME from "../../../theme";

export const BREAKPOINT_KLEIN = 640;      // darunter: Auswahlmenues statt Reiter, Karten statt Tabellen
export const BREAKPOINT_MITTEL = 1024;    // wie im Rest der App

export function useBreite() {
  const [b, setB] = useState(typeof window === "undefined" ? 1280 : window.innerWidth);
  useEffect(() => {
    const f = () => setB(window.innerWidth);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return b;
}

// Phase 1 der Neugestaltung (nur Darstellung): einklappbarer Bereich (Filter, Detailabschnitte). Ohne JS-Zustand von aussen nutzbar.
export function Einklappbar({ titel, zusatz, offenStart = false, children, rechts }) {
  const [offen, setOffen] = useState(offenStart);
  return (
    <section style={{ background: THEME.colors.surface, borderRadius: THEME.radius.xl, marginBottom: 12, minWidth: 0 }}>
      <button type="button" aria-expanded={offen} onClick={() => setOffen(o => !o)}
        style={{ width: "100%", minHeight: 48, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, textAlign: "left", background: "transparent", border: "none", color: THEME.colors.text,
          padding: "10px 16px", cursor: "pointer", fontSize: THEME.fontSize.md, fontWeight: 600 }}>
        <span style={{ minWidth: 0 }}>{titel}{zusatz ? <span style={{ color: THEME.colors.textSecondary, fontWeight: 400, marginLeft: 8, fontSize: THEME.fontSize.sm }}>{zusatz}</span> : null}</span>
        <span aria-hidden="true" style={{ color: THEME.colors.textSecondary }}>{offen ? "▲" : "▼"}</span>
      </button>
      {offen && <div style={{ padding: "0 16px 16px" }}>{rechts}{children}</div>}
    </section>
  );
}

export const geld = (n, vorzeichen = false) => (n === null || n === undefined || n === "" || Number.isNaN(Number(n)) ? "–"
  : `${vorzeichen && Number(n) > 0 ? "+" : ""}${Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

export function Seitenkopf({ titel, untertitel, rechts }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl || 22, fontWeight: 700 }}>{titel}</h2>
        {untertitel && <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginTop: 4, maxWidth: 760 }}>{untertitel}</div>}
      </div>
      {rechts && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>{rechts}</div>}
    </div>
  );
}

// Dauerhaft sichtbarer Hinweis an jeder Stelle der Parallelbuchhaltung / des Kanzleivergleichs
export function PbBanner({ text }) {
  return (
    <div role="note" style={{ display: "flex", gap: 10, alignItems: "flex-start", border: `1px solid ${THEME.colors.warning}`, borderLeft: `4px solid ${THEME.colors.warning}`, background: THEME.colors.surface,
      borderRadius: THEME.radius.md, padding: "8px 12px", marginBottom: 16, color: THEME.colors.text }}>
      <span aria-hidden="true">🧪</span>
      <div>
        <strong style={{ color: THEME.colors.warning }}>INTERN · VORLÄUFIG · nicht maßgeblich</strong>
        <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
          {text || "Die Buchhaltung des Steuerberaters bleibt maßgeblich. Nichts wird an DATEV, den Steuerberater oder das Finanzamt übermittelt."}
        </div>
      </div>
    </div>
  );
}

export function Kachel({ titel, wert, farbe, hinweis, onClick, gross, rahmen }) {
  const inhalt = (
    <>
      <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{titel}</div>
      <div style={{ color: farbe || THEME.colors.text, fontSize: gross ? 28 : 22, fontWeight: 700, margin: "4px 0", overflowWrap: "anywhere" }}>{wert}</div>
      {hinweis && <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, overflowWrap: "anywhere" }}>{hinweis}</div>}
    </>
  );
  const stil = { textAlign: "left", background: THEME.colors.surface, borderRadius: THEME.radius.lg, padding: "14px 16px", border: `1px solid ${rahmen || THEME.colors.border}`, minWidth: 0, width: "100%", boxSizing: "border-box", color: THEME.colors.text };
  return onClick ? <button onClick={onClick} style={{ ...stil, cursor: "pointer" }}>{inhalt}</button> : <div style={stil}>{inhalt}</div>;
}

export const kachelRaster = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 };

export function Abschnitt({ titel, rechts, children }) {
  return (
    <section style={{ background: THEME.colors.surface, borderRadius: THEME.radius.xl, padding: "16px 18px", marginBottom: 16, minWidth: 0 }}>
      {(titel || rechts) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: THEME.fontSize.lg || 17, color: THEME.colors.text }}>{titel}</h3>{rechts}
        </div>
      )}
      {children}
    </section>
  );
}

export function Pille({ text, farbe, aktiv, onClick, zahl }) {
  const f = farbe || THEME.colors.textMuted;
  return (
    <button onClick={onClick} aria-pressed={aktiv} style={{ border: `1px solid ${aktiv ? f : THEME.colors.border}`, background: aktiv ? THEME.colors.surfaceMuted : "transparent", color: aktiv ? THEME.colors.text : THEME.colors.textSecondary,
      borderRadius: 999, padding: "5px 12px", fontSize: THEME.fontSize.sm, cursor: onClick ? "pointer" : "default", whiteSpace: "nowrap" }}>
      {text}{zahl !== undefined && zahl !== null ? <span style={{ marginLeft: 6, color: f, fontWeight: 700 }}>{zahl}</span> : null}
    </button>
  );
}
